import { Box, Typography, useTheme } from '@mui/material'
import { type FC, useEffect, useRef, useState } from 'react'
import { BBox } from './BBox'
import { Crosshair } from './Crosshair'
import { InteractiveBBox } from './InteractiveBBox'
import { useBBoxCreation } from './useBBoxCreation'
import { useBBoxInteractions } from './useBBoxInteractions'

type Props = {
  labelImageUrl: string
  otherBBoxes: {
    id: string
    name: string
    x: number
    y: number
    width: number
    height: number
  }[]
  selectedBBox: {
    name: string
    x: number
    y: number
    width: number
    height: number
  }
  onBBoxChange: {
    setX: (x: number) => void
    setY: (y: number) => void
    setWidth: (w: number) => void
    setHeight: (h: number) => void
  }
  onCancel?: () => void
  color?: string
  resizable?: boolean
}

/** ラベル画像上で、検査箇所のBBoxを表示・編集する */
export const BBoxSelector: FC<Props> = ({
  labelImageUrl,
  otherBBoxes,
  selectedBBox,
  onBBoxChange,
  onCancel,
  color: colorProp,
  resizable = true,
}) => {
  const theme = useTheme()
  const color = colorProp ?? theme.palette.error.main

  const [mousePosition, setMousePosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const interactions = useBBoxInteractions(
    containerRef,
    selectedBBox,
    onBBoxChange,
  )
  const creation = useBBoxCreation(containerRef, selectedBBox, onBBoxChange)
  const creating = creation.creating

  useEffect(() => {
    if (!onCancel) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onCancel()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [onCancel])

  const showCrosshair = resizable ? creating : true

  return (
    <Box width="100%">
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 'auto',
          cursor: 'crosshair',
        }}
        ref={containerRef}
        onPointerDown={creation.onPointerDown}
        onPointerMove={e => {
          const rect = (
            e.currentTarget as HTMLDivElement
          ).getBoundingClientRect()
          setMousePosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          })
          if (resizable) interactions.moveWhileActive(e)
          creation.onPointerMove(e)
        }}
        onPointerUp={e => {
          if (resizable) interactions.end(e)
          creation.onPointerUp(e)
        }}
        onPointerCancel={e => {
          if (resizable) interactions.end(e)
          creation.cancel()
        }}
        onMouseLeave={() => {
          setMousePosition(null)
          if (resizable) interactions.end()
          creation.cancel()
        }}
      >
        <img
          src={labelImageUrl}
          alt="ラベル画像"
          width="100%"
          crossOrigin="anonymous"
          style={{
            objectFit: 'contain',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          draggable={false}
        />

        {otherBBoxes.map(box => (
          <BBox key={box.id} box={box} />
        ))}

        {resizable ? (
          <InteractiveBBox
            x={selectedBBox.x}
            y={selectedBBox.y}
            width={selectedBBox.width}
            height={selectedBBox.height}
            color={color}
            dashed={creating}
            label={selectedBBox.name}
            onPointerDownMove={e => interactions.begin(e, 'move')}
            onPointerMoveActive={e => interactions.moveWhileActive(e)}
            onPointerUp={e => interactions.end(e)}
            onPointerDownHandle={(e, dir) => interactions.begin(e, dir)}
          />
        ) : (
          selectedBBox.width > 0 &&
          selectedBBox.height > 0 && (
            <Box
              sx={{
                position: 'absolute',
                border: `2px ${creating ? 'dashed' : 'solid'} ${color}`,
                left: `${selectedBBox.x * 100}%`,
                top: `${selectedBBox.y * 100}%`,
                width: `${selectedBBox.width * 100}%`,
                height: `${selectedBBox.height * 100}%`,
                pointerEvents: 'none',
              }}
            >
              {selectedBBox.name.length > 0 && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '-24px',
                    left: '0',
                    backgroundColor: color,
                    color: 'white',
                    padding: '2px 8px',
                    fontSize: '12px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                  }}
                >
                  {selectedBBox.name}
                </Typography>
              )}
            </Box>
          )
        )}

        {showCrosshair && mousePosition && (
          <Crosshair x={mousePosition.x} y={mousePosition.y} color={color} />
        )}
      </Box>
    </Box>
  )
}
